/**
 * Sourcing / buy-side limit orders — the fee surface + buyer-order types shared
 * by web + mobile, so the two shells can't drift. The twin of `askaPlus.ts`.
 *
 * SOURCE OF TRUTH IS THE SERVER (CLAUDE.md invariant 4): `functions/src/sourcing`
 * composes the fees from env (`sourcingConfig()`), `/api/health` serves them as
 * `sourcingPlan`, and this module is a LIVE BINDING seeded with the launch
 * defaults and re-pointed by `hydrateSourcingPlan()`, which the boot fetch in
 * `askaServices.ts` calls. Nothing is ever charged from these numbers — the
 * server snapshots its own fees at confirm-time.
 *
 * ── `live` IS THE ONLY THING A "place order" CTA MAY BRANCH ON ─────────────
 * `live: false` (the default, and what production serves until
 * SOURCING_ORDERS_LIVE is armed) means: render the pitch + the fee lines all you
 * like — they're true — but do NOT render a working place button, because
 * `POST /sourcing/orders` answers `{configured:false}` and the person who
 * clicked concludes it's broken. Unlike membership/seats, sourcing is NOT
 * native-restricted: a sourcing order buys a physical diamond (goods), not a
 * digital subscription, so it lives on BOTH shells in full.
 */

export type GuaranteeKind = 'percent' | 'flat'

export interface SourcingPlan {
  /** Can a sourcing order be placed right now? False while the flag is inert. */
  live: boolean
  /**
   * Is the agentic-sourcing RFQ fan-out armed? (docs/ASKA-PLUS-AGENTIC-SOURCING.md)
   * Independent of `live` — the on-network limit-order desk can be on while the
   * want-list / RFQ surface is still "coming soon". False (default, and what prod
   * serves until SOURCING_RFQ_LIVE) ⇒ the WantListPanel shows the pitch, no CTA. */
  rfqLive: boolean
  /** Flat sourcing fee for a non-member, USD. $0 = free (the launch default). */
  sourcingFeeUsd: number
  /** Flat sourcing fee for an Aska+ member, USD (free). */
  sourcingFeeMemberUsd: number
  /** The optional guarantee is priced as a percent OR a flat dollar amount. */
  guaranteeKind: GuaranteeKind
  /** Guarantee value for a non-member (percent of base, or flat USD). */
  guaranteeValue: number
  /** Guarantee value for an Aska+ member (lower). */
  guaranteeMemberValue: number
  /** Card-rail surcharge percent the buyer pays if they pay by card. */
  cardSurchargePct: number
  /** How many open sourcing orders one member may hold at once. */
  maxOpenPerMember: number
  /** RFQ fan-out cap — the most suppliers one want ever contacts (display only). */
  rfqMaxSuppliers: number
  /** RFQ follow-up rounds per supplier thread (display only). */
  rfqMaxRounds: number
}

/** Launch defaults (docs/PRICING-MODEL.md). Display seed only — the server is
 *  authoritative and re-points this at boot. Sourcing is FREE by default. */
export let SOURCING_PLAN: SourcingPlan = {
  live: false,
  rfqLive: false,
  sourcingFeeUsd: 0,
  sourcingFeeMemberUsd: 0,
  guaranteeKind: 'percent',
  guaranteeValue: 3.5,
  guaranteeMemberValue: 2,
  cardSurchargePct: 3,
  maxOpenPerMember: 25,
  rfqMaxSuppliers: 8,
  rfqMaxRounds: 2,
}

/**
 * Re-point the plan from `/api/health`, then broadcast `aska:sourcing-changed`.
 * VALIDATED, not trusted: a malformed payload leaves the seed in place, and a
 * missing `live` is FALSE — the safe direction is "no place CTA".
 */
export function hydrateSourcingPlan(raw: unknown): void {
  if (!raw || typeof raw !== 'object') return
  const p = raw as Partial<Record<keyof SourcingPlan, unknown>>
  const num = (v: unknown, fallback: number): number =>
    (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : fallback)
  const next: SourcingPlan = {
    live: p.live === true,
    rfqLive: p.rfqLive === true,
    sourcingFeeUsd: num(p.sourcingFeeUsd, SOURCING_PLAN.sourcingFeeUsd),
    sourcingFeeMemberUsd: num(p.sourcingFeeMemberUsd, SOURCING_PLAN.sourcingFeeMemberUsd),
    guaranteeKind: p.guaranteeKind === 'flat' ? 'flat' : 'percent',
    guaranteeValue: num(p.guaranteeValue, SOURCING_PLAN.guaranteeValue),
    guaranteeMemberValue: num(p.guaranteeMemberValue, SOURCING_PLAN.guaranteeMemberValue),
    cardSurchargePct: num(p.cardSurchargePct, SOURCING_PLAN.cardSurchargePct),
    maxOpenPerMember: num(p.maxOpenPerMember, SOURCING_PLAN.maxOpenPerMember),
    rfqMaxSuppliers: num(p.rfqMaxSuppliers, SOURCING_PLAN.rfqMaxSuppliers),
    rfqMaxRounds: num(p.rfqMaxRounds, SOURCING_PLAN.rfqMaxRounds),
  }
  if (JSON.stringify(next) === JSON.stringify(SOURCING_PLAN)) return
  SOURCING_PLAN = next
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('aska:sourcing-changed'))
  }
}

/**
 * PER-USER agentic-sourcing enrollment — the server's word (from `/me`
 * `user.sourcingEnrolled`), which is `SOURCING_RFQ_LIVE armed AND (admin OR on the
 * buyer allowlist)`. FALSE by default, so a non-enrolled buyer never even sees the
 * ＋ (the tray/board/entry points all gate on `canUseAgenticSourcing()`). Set by
 * `identity.ts::loadProfile` when /me lands and cleared on sign-out. The `/health`
 * plan is GLOBAL (is the feature armed at all); THIS is per-caller.
 */
let SOURCING_ENROLLED = false
export function hydrateSourcingEnrolled(v: unknown): void {
  const next = v === true
  if (next === SOURCING_ENROLLED) return
  SOURCING_ENROLLED = next
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('aska:sourcing-changed'))
  }
}

/** THE gate for a working "place a sourcing order" CTA — the rail is armed
 *  server-side. (No native restriction: sourcing buys goods, not a
 *  subscription.) A false value means show the pitch + "coming soon". */
export function canPlaceSourcingOrders(): boolean {
  return SOURCING_PLAN.live
}

/**
 * Read the `?sourcing=1` / `?sourcing=0` preview latch into localStorage, and
 * report whether it's set. Mirrors the `?app=1` app-mode latch
 * (`mobile/lib/appMode.ts`) so a previewer can force the RFQ surface ON before
 * the server flag arms, WITHOUT the surface ever showing on prod (where no
 * VITE var is set and nobody appends `?sourcing=1`). Idempotent; SSR-safe;
 * swallows storage errors (private mode) — the other two signals still work.
 *
 * Deliberately localStorage (persistent within the browser), not session:
 * a previewer clicks `?sourcing=1` once and every subsequent page keeps the
 * surface armed until they clear it with `?sourcing=0`.
 */
const SOURCING_LATCH_KEY = 'diaex-sourcing-preview'

function sourcingPreviewLatched(): boolean {
  if (typeof window === 'undefined') return false
  try {
    const q = new URLSearchParams(window.location.search).get('sourcing')
    if (q === '1') window.localStorage.setItem(SOURCING_LATCH_KEY, '1')
    else if (q === '0') window.localStorage.removeItem(SOURCING_LATCH_KEY)
    return window.localStorage.getItem(SOURCING_LATCH_KEY) === '1'
  } catch {
    // storage unavailable — fall back to the raw query for this load only.
    try {
      return new URLSearchParams(window.location.search).get('sourcing') === '1'
    } catch {
      return false
    }
  }
}

/** THE gate for the agentic-sourcing WANT-LIST / RFQ surface
 *  (docs/ASKA-PLUS-AGENTIC-SOURCING.md). True when ANY of:
 *   1. the hydrated server plan says so (`SOURCING_RFQ_LIVE` armed) — prod's path;
 *   2. the build carries `VITE_SOURCING_RFQ_LIVE === 'true'` — a preview build;
 *   3. the `?sourcing=1` localStorage preview latch is set — a live preview.
 *  False (prod's only reachable state today) ⇒ the WantListPanel shows the
 *  pitch + "coming soon" and every entry point / tray self-hides — never a
 *  working build-a-list CTA (the routes answer {rfq_not_configured}). */
export function canUseAgenticSourcing(): boolean {
  // PER-USER: the server (via /me) says this caller may use it — armed AND (admin
  // OR allowlisted). This is the ONLY path that opens the ＋ on a real prod/preview
  // build, so a non-enrolled buyer never sees any sourcing entry point.
  if (SOURCING_ENROLLED) return true
  // LOCAL-DEV escape hatches ONLY (import.meta.env.DEV is false in every `vite
  // build`, incl. preview channels + prod) — so a curious prod user can't reveal
  // the ＋ with a build flag or `?sourcing=1`; only the server's word counts there.
  if (import.meta.env.DEV) {
    if (import.meta.env.VITE_SOURCING_RFQ_LIVE === 'true') return true
    if (sourcingPreviewLatched()) return true
  }
  return false
}

/** "$40" / "Free" — one place, so the shells describe a flat fee the same way. */
export function formatSourcingFee(usd: number): string {
  if (!Number.isFinite(usd) || usd <= 0) return 'Free'
  return usd % 1 === 0 ? `$${usd}` : `$${usd.toFixed(2)}`
}

/** The guarantee line for a member/non-member — "2%" or "$30". */
export function formatGuarantee(member: boolean, plan: SourcingPlan = SOURCING_PLAN): string {
  const v = member ? plan.guaranteeMemberValue : plan.guaranteeValue
  return plan.guaranteeKind === 'percent'
    ? `${v % 1 === 0 ? v : v.toFixed(1)}%`
    : formatSourcingFee(v)
}

/* ─────────────────────────── buyer order model ───────────────────────────── */

export type SourcingLimitBasis = 'ppc' | 'total'

/** Statuses the server serves (never inferred client-side — spec parity). */
export type SourcingStatus =
  | 'open' | 'standing' | 'matched' | 'confirming'
  | 'purchased' | 'partial' | 'cancelled' | 'expired'

/** The buyer-safe match projection (`projectOrderForBuyer` on the server — NO
 *  supplier identity, only our stockId + the quote). */
export interface SourcingMatch {
  stockId: string
  quotedBase: number
  quotedPerCarat: number
  sourcingFeeUsd: number
  guaranteeFeeUsd: number
  guaranteeSelected: boolean
  cardSurchargeUsd: number
  member: boolean
  quotedTotal: number
  quotedAt: string
}

/** The buyer-facing sourcing order (the projection the API returns). */
export interface SourcingOrderView {
  id: string
  spec: Record<string, unknown>
  limitBasis: SourcingLimitBasis
  limitValue: number
  certifiedOnly: boolean
  quantity: number
  remainingQty: number
  status: SourcingStatus
  autoConfirm: { enabled: boolean; capUsd: number | null }
  expiry: { mode: 'gtc' | 'date'; at: string | null }
  match: SourcingMatch | null
  broadcast: { status: string; round: number; askedCount: number } | null
  fills: { orderId: number | string; stockId: string; at: string }[]
  siblingGroupId: string | null
  createdAt: string
  updatedAt: string
}

/** Is this order still working (can be edited/cancelled)? */
export function isWorkingOrder(s: SourcingStatus): boolean {
  return s === 'open' || s === 'standing'
}

/** Is there a match the buyer can review + confirm right now? */
export function isConfirmable(o: Pick<SourcingOrderView, 'status' | 'match'>): boolean {
  return o.status === 'matched' && !!o.match
}

/** A short, human status label — one vocabulary across both shells. */
export function sourcingStatusLabel(s: SourcingStatus): string {
  switch (s) {
    case 'open': return 'Searching'
    case 'standing': return 'Standing'
    case 'matched': return 'Match found'
    case 'confirming': return 'Confirming'
    case 'purchased': return 'Purchased'
    case 'partial': return 'Partially filled'
    case 'cancelled': return 'Cancelled'
    case 'expired': return 'Expired'
    default: return s
  }
}
